<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-toolbar class="md-success">
        <div class="md-toolbar-row">
          <div class="md-toolbar-section-start">
            <hierarchy-menu></hierarchy-menu>
          </div>

          <md-button class="md-simple md-just-icon md-round" @click="openModal">
            <i style="color: #ffffff !important;" class="fas fa-cog"></i>
          </md-button>
        </div>
      </md-toolbar>
      <br />
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="blue">
        <template slot="header">
          <div class="card-icon">
            <md-icon>power</md-icon>
          </div>
          <p class="category">{{$lang.charts.voltage}}</p>
          <h3 class="title">
            <animated-number
              :value="averageVoltage"
              :formatValue="voltageFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.U.MIN"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.U.AVG"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.U.MAX"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              {{$lang.general.per}} {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon">
            <i class="fas fa-bolt"></i>
          </div>
          <p class="category">{{$lang.charts.current}}</p>
          <h3 class="title">
            <animated-number
              :value="averageCurrent"
              :formatValue="currentFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.I.MIN"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.I.AVG"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.I.MAX"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              {{$lang.general.per}} {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>wb_incandescent</md-icon>
          </div>
          <p class="category">{{$lang.charts.active_power}}</p>
          <h3 class="title">
            <animated-number
              :value="activePower"
              :formatValue="activePowerFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.P.MIN"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.P.AVG"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.P.MAX"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              {{$lang.general.per}} {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="warning">
        <template slot="header">
          <div class="card-icon">
            <md-icon>equalizer</md-icon>
          </div>
          <p
            class="category"
          >{{$lang.charts.active_energy}} {{$lang.general.per}} {{localizedDateRange()}}</p>
          <h3 class="title">
            <animated-number
              :value="energyImport"
              :formatValue="energyImportFormat"
              :duration="1000"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            {{$lang.charts.total_energy}}
            <b>
              <animated-number
                :value="statisticData.E_I.total"
                :formatValue="energyImportFormat"
                :duration="1000"
              />
            </b>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout">
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-75">
        <md-card>
          <md-card-header class="md-card-header-icon md-card-header-warning">
            <div class="card-icon">
              <md-icon>equalizer</md-icon>
            </div>
            <h4 class="title">Pomer spotrebovanej energie</h4>
          </md-card-header>
          <md-card-content>
            <highcharts class="pie-chart" :options="chartOptions" ref="pieChart"></highcharts>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-medium-size-33 md-xsmall-size-100 md-size-25">
        <pricing-card>
          <h6 slot="category" class="category">Osvetlenie</h6>
          <div slot="icon">
            <img
              class="appliance-icons"
              :class="{'appliance-on-light': NILM.light_on}"
              src="/img/dashboard/light-bulb.png"
            />
            <h4 slot="title" class="title">Energia {{light.energy_wh.toFixed(2)}} Wh / {{light.g_co2.toFixed(2)}} g/Co2</h4>
          </div>
        </pricing-card>

        <pricing-card>
          <h6 slot="category" class="category">Ventilátor</h6>
          <div slot="icon">
            <img
              class="appliance-icons"
              :class="{'appliance-on-fan': NILM.fan_on}"
              src="/img/dashboard/fan.png"
            />
            <h4 slot="title" class="title">Energia {{fan.energy_wh.toFixed(2)}} Wh / {{fan.g_co2.toFixed(2)}} g/Co2</h4>
          </div>
        </pricing-card>

        <pricing-card>
          <h6 slot="category" class="category">Kanvica</h6>
          <div slot="icon">
            <img
              class="appliance-icons"
              :class="{'appliance-on-kettle': NILM.kettle_on}"
              src="/img/dashboard/kettle.png"
            />
            <h4 slot="title" class="title">Energia {{kettle.energy_wh.toFixed(2)}} Wh / {{kettle.g_co2.toFixed(2)}} g/Co2</h4>
          </div>
        </pricing-card>
      </div>
    </div>

    <modal v-if="showModal">
      <template slot="header">
        <h4 class="modal-title">{{$lang.titles.dashboard_settings}}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="modalClose">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <h4 class="card-title">{{$lang.titles.statistics_report}}</h4>
        <md-radio v-model="selectedDateRange" value="today">{{$lang.keys.today_stats}}</md-radio>
        <md-radio v-model="selectedDateRange" value="week">{{$lang.keys.week}}</md-radio>
        <md-radio v-model="selectedDateRange" value="month">{{$lang.keys.month}}</md-radio>
        <md-radio v-model="selectedDateRange" value="year">{{$lang.keys.year}}</md-radio>
      </template>

      <template slot="footer">
        <md-button class="md-primary" @click="saveModalData">{{$lang.keys.save}}</md-button>
        <md-button class="md-simple" @click="modalClose">{{$lang.keys.close}}</md-button>
      </template>
    </modal>

    <snack-bar ref="snackbar"></snack-bar>
  </div>
</template>

<script>
import { Modal } from "@/components";
import snackBar from "@/components/SnackBar";
import hierarchyMenu from "@/components/HierarchyMenu";
import AnimatedNumber from "animated-number-vue";

import chartOptions from "@/config/charts/column";
import axios from "axios";
import { getSelectedDeviceToken, getUserToken } from "@/storage/memoryStorage";

import format from "date-fns/format";
import subHours from "date-fns/sub_hours";
import subDays from "date-fns/sub_days";
import start_of_today from "date-fns/start_of_today";
import end_of_today from "date-fns/end_of_today";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import startOfMonth from "date-fns/start_of_month";
import endOfMonth from "date-fns/end_of_month";
import startOfYear from "date-fns/start_of_year";
import endOfYear from "date-fns/end_of_year";
import endOfTomorrow from "date-fns/end_of_tomorrow";
import startOfHour from "date-fns/start_of_hour";

import { StatsCard, GlobalSalesCard, PricingCard } from "@/components";
import { startOfDay, subMonths } from "date-fns";

export default {
  components: {
    "hierarchy-menu": hierarchyMenu,
    "snack-bar": snackBar,
    Modal,
    AnimatedNumber,
    StatsCard,
    GlobalSalesCard,
    PricingCard
  },

  data() {
    return {
      animationDuration: 500,
      showModal: false,

      selectedDateRange: "",

      averageVoltage: "",
      averageCurrent: "",
      activePower: "",
      energyImport: "",

      kwhToEuro: 0.0664,
      versusPrice: 0.0008156,
      kwhToKgCo2: 1.17,

      NILM: {
        light_on: false,
        fan_on: false,
        kettle_on: false
      },

      light: {
        energy_wh: 0.00001,
        g_co2: 0,
        price: 0,
        sliced: false
      },

      fan: {
        energy_wh: 0.00001,
        g_co2: 0,
        price: 0,
        sliced: false
      },

      kettle: {
        energy_wh: 0.00001,
        g_co2: 0,
        price: 0,
        sliced: false
      },

      interval: null,

      chartOptions: null,

      statisticData: {
        loading: false,
        U: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        I: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        P: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        E_I: {
          start: 0,
          total: 0
        }
      }
    };
  },

  computed: {
    WSmessage() {
      return this.$store.getters.getDataMessage;
    },
    selectedDate() {
      return this.$store.getters.getDateRange;
    },
    selectedDevice() {
      return this.$store.getters.getSelectedDevice;
    },
    deviceList() {
      return this.$store.getters.getDeviceList;
    },
    statsDate() {
      return this.$store.getters.getSettings.statsDate;
    },
    notificationList() {
      return this.$store.getters.getNotificationList;
    }
  },

  watch: {
    WSmessage: function(payload, oldPayload) {
      let dividerAVG = 0;

      if (payload.U.L1 > 0) {
        dividerAVG++;
      }

      if (payload.U.L2 > 0) {
        dividerAVG++;
      }

      if (payload.U.L3 > 0) {
        dividerAVG++;
      }

      this.averageVoltage =
        (payload.U.L1 + payload.U.L2 + payload.U.L3) / dividerAVG;
      this.averageCurrent = payload.I.L1 + payload.I.L2 + payload.I.L3;
      this.activePower = payload.P.L1 + payload.P.L2 + payload.P.L3;
      this.statisticData.E_I.total =
        payload.E_I.L1 + payload.E_I.L2 + payload.E_I.L3;
      this.energyImport =
        this.statisticData.E_I.total - this.statisticData.E_I.start;

      if (payload.P.L1 < 20) {
        this.NILM.light_on = false;
        this.NILM.fan_on = false;
        this.NILM.kettle_on = false;
      } 

        const { chart } = this.$refs.pieChart;

        if (this.NILM.light_on) {
          const lightWhPerSecond = 0.0148;
          this.light.energy_wh += lightWhPerSecond;
          this.light.g_co2 = (this.light.energy_wh / 1000) * (this.kwhToKgCo2 * 1000);
          this.light.sliced = true;
        } else {
          this.light.sliced = false;
        }

        if (this.NILM.fan_on) {
          const fanWhPerSecond = 0.01;
          this.fan.energy_wh += fanWhPerSecond;
          this.fan.g_co2 = (this.fan.energy_wh / 1000) * (this.kwhToKgCo2 * 1000);
          this.fan.sliced = true;
        } else {
          this.fan.sliced = false;
        }

        if (this.NILM.kettle_on) {
          const kettleWhPerSecond = 0.5310344828;
          this.kettle.energy_wh += kettleWhPerSecond;
          this.kettle.g_co2 = (this.kettle.energy_wh / 1000) * (this.kwhToKgCo2 * 1000);
          this.kettle.sliced = true;
        } else {
          this.kettle.sliced = false;
        }

        chart.series[0].setData(
          [
            {
              name: "Osvetlenie",
              y: this.light.energy_wh,
              water_l: ((this.light.energy_wh / 1000) * this.kwhToEuro) / this.versusPrice,
              price: (this.light.energy_wh / 1000) * this.kwhToEuro,
              sliced: this.light.sliced
            },
            {
              name: "Ventilátor",
              y: this.fan.energy_wh,
              water_l: ((this.fan.energy_wh / 1000) * this.kwhToEuro) / this.versusPrice,
              price: (this.fan.energy_wh / 1000) * this.kwhToEuro,
              sliced: this.fan.sliced
            },
            {
              name: "Rýchlovarná kanvica",
              y: this.kettle.energy_wh,
              water_l: ((this.kettle.energy_wh / 1000) * this.kwhToEuro) / this.versusPrice,
              price: (this.kettle.energy_wh / 1000) * this.kwhToEuro,
              sliced: this.kettle.sliced
            }
          ],
          true
        );
      

    },
    selectedDevice: function(newDevice, oldDevice) {
      if (newDevice.mac !== oldDevice.mac) {
        this.getStatisticsData();
        this.getEnergyData();
      }
    },
    notificationList: function(payload, oldPayload) {
      try {
        const lastNotification = payload[0];

        if (lastNotification.type === "NILM") {
          switch (lastNotification.data.ID_appliance) {
            case 139669:
              this.NILM.light_on =
                lastNotification.data.event === "ON" ? true : false;
              break;
            case 139670:
              this.NILM.fan_on =
                lastNotification.data.event === "ON" ? true : false;
              break;
            case 139673:
              this.NILM.kettle_on =
                lastNotification.data.event === "ON" ? true : false;
              break;
          }
        }
      } catch (err){
        console.log("Notification error parse");
        console.log(err);
      }
    }
  },

  created() {
    this.selectedDateRange = this.statsDate.selector;

    this.chartOptions = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        type: "pie"
      },
      title: {
        text: ""
      },
      tooltip: {
        pointFormat: "<b>{point.y:.3f} Wh</b> ({point.percentage:.1f}%)"
      },
      plotOptions: {
        pie: {
          slicedOffset: 20,
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format:
              "{point.name} ({point.percentage:.1f}%)<br>Energia {point.y:.2f}Wh / Cena {point.price:.2f}€<br>To je približne {point.water_l:.2f}l vody"
          },
          showInLegend: true
        },
        series: {
          dataLabels: {
            style: {
              fontSize: "14px"
            }
          }
        }
      },
          "credits": {
        "enabled": false
    },
      legend: {
        verticalAlign: "top"
      },
      exporting: {
        enabled: false
      },
      series: [
        {
          colorByPoint: true,
          data: [
            {
              name: "Osvetlenie",
              y: this.light.energy_wh,
              water_l: 0,
              price: 0,
              sliced: this.light.sliced,
              color: "#66bb6a"
            },
            {
              name: "Ventilátor",
              y: this.fan.energy_wh,
              water_l: 0,
              price: 0,
              sliced: this.fan.sliced,
              color: "#039be5"
            },
            {
              name: "Rýchlovarná kanvica",
              y: this.kettle.energy_wh,
              water_l: 0,
              price: 0,
              sliced: this.kettle.sliced,
              color: "#ff7043"
            }
          ]
        }
      ]
    };

    this.chartOptions.chart.events = {
      redraw: function(event) {
        this.hideLoading();
      }
    };
  },

  mounted() {
    let compStatsDateRange = this.getTSFromDateRange(this.selectedDateRange);

    if (
      compStatsDateRange.from !== this.statsDate.from ||
      compStatsDateRange.to !== this.statsDate.to
    ) {
      compStatsDateRange.selector = this.selectedDateRange;
      this.$store.commit("setstatsDate", compStatsDateRange);
    }

    this.getEnergyData();
    this.getStatisticsData();

    this.interval = setInterval(function(){
      this.getStatisticsData();
    }.bind(this), 300000);
  },

  beforeDestroy(){
    clearInterval(this.interval);
  },

  methods: {
    localizedDateRange() {
      switch (this.selectedDateRange) {
        case "today":
          return this.$lang.keys.today_stats;
        case "week":
          return this.$lang.keys.this_week;
        case "month":
          return this.$lang.keys.this_month;
        case "year":
          return this.$lang.keys.this_year;
      }
    },

    getTSFromDateRange(dateRange) {
      let statDate = {
        selector: dateRange,
        from: 0,
        to: 0
      };
      switch (dateRange) {
        case "today":
          statDate.from = format(start_of_today(), "X");
          statDate.to = format(end_of_today(), "X");
          break;
        case "week":
          statDate.from = format(
            startOfWeek(start_of_today(), {
              weekStartsOn: 1
            }),
            "X"
          );
          statDate.to = format(
            endOfWeek(start_of_today(), {
              weekStartsOn: 1
            }),
            "X"
          );
          break;
        case "month":
          statDate.from = format(startOfMonth(start_of_today()), "X");
          statDate.to = format(endOfMonth(start_of_today()), "X");
          break;
        case "year":
          statDate.from = format(startOfYear(start_of_today()), "X");
          statDate.to = format(endOfYear(start_of_today()), "X");
          break;
      }
      return statDate;
    },

    saveModalData: function() {
      let compStatsDateRange = this.getTSFromDateRange(this.selectedDateRange);

      if (
        this.statsDate.from !== compStatsDateRange.from ||
        this.statsDate.to !== compStatsDateRange.to
      ) {
        compStatsDateRange.selector = this.selectedDateRange;
        this.$store.commit("setstatsDate", compStatsDateRange);
        this.getStatisticsData();
        this.getEnergyData();
      }
      this.modalClose();
    },

    openModal: function() {
      this.showModal = true;
    },

    modalClose: function() {
      this.showModal = false;
    },

    getStatisticsData() {
      this.statisticData.loading = true;
      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/statistics",
        data: {
          token: getSelectedDeviceToken(this.selectedDevice.mac),
          data: "U,I,P",
          phase: "CUM",
          from: this.statsDate.from,
          to: this.statsDate.to,
          statData: "MIN,MAX,AVG"
        },
        withCredentials: true
      })
        .then(response => {
          if (response.data.status === "OK") {
            this.statisticData.U.MIN = response.data.data.U.CUM.MIN;
            this.statisticData.U.AVG = response.data.data.U.CUM.AVG;
            this.statisticData.U.MAX = response.data.data.U.CUM.MAX;

            this.statisticData.I.MIN = response.data.data.I.CUM.MIN;
            this.statisticData.I.AVG = response.data.data.I.CUM.AVG;
            this.statisticData.I.MAX = response.data.data.I.CUM.MAX;

            this.statisticData.P.MIN = response.data.data.P.CUM.MIN;
            this.statisticData.P.AVG = response.data.data.P.CUM.AVG;
            this.statisticData.P.MAX = response.data.data.P.CUM.MAX;

            this.statisticData.loading = false;
          }
        })
        .catch(error => {
          console.log(error);
          if (error.response.data.status == "FAIL") {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    getEnergyData() {
      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/profile",
        data: {
          token: getSelectedDeviceToken(this.selectedDevice.mac),
          data: "E_I",
          phase: "L1,L2,L3",
          from: this.statsDate.from,
          to: Number(this.statsDate.from) + 60,
          cumulative: true
        },
        withCredentials: true
      })
        .then(response => {
          if (response.data.status === "OK") {
            this.statisticData.E_I.start = response.data.data[0].E_I.cumulative;
          } else {
            this.$refs.snackbar.notify("API ERROR");
            console.log(error);
          }
        })
        .catch(error => {
          console.log(error);
          if (error.response.data.status == "FAIL") {
            if (error.response.data.code == 610) {
              this.statisticData.E_I.start = 0;
              this.$refs.snackbar.notify(
                this.$lang.messages.energy_not_found_for_selected_time
              );
            } else {
              this.$refs.snackbar.notify(
                this.$lang.messages.api_communication_error
              );
            }
          }
        });
    },

    voltageFormat(value) {
      return `${Number(value).toFixed(1)} V`;
    },

    currentFormat(value) {
      if (value > 1000) {
        return `${Number(value / 1000).toFixed(2)} kA`;
      } else if (value > 100) {
        return `${Number(value).toFixed(1)} A`;
      } else {
        return `${Number(value).toFixed(2)} A`;
      }
    },

    activePowerFormat(value) {
      if (value > 1000) {
        return `${Number(value / 1000).toFixed(2)} kW`;
      } else {
        return `${Number(value).toFixed(1)} W`;
      }
    },

    energyImportFormat(value) {
      if (value > 100000) {
        return `${Number(value / 1000).toFixed(2)} kWh`;
      } else {
        return `${Number(value).toFixed(2)} Wh`;
      }
    }
  }
};
</script>

<style scoped>
#map {
  margin: 0;
  max-height: 500px;
}

.normal-header {
  padding: 0 !important;
  min-height: 0px;
}

/* .md-button.md-simple i,
.md-button.md-simple:hover i {
  color: #ffffff !important;
} */

.md-toolbar {
  position: inherit;
}

.modal-header {
  opacity: 1;
}

.stats-layout {
  justify-content: center;
  text-align: center;
}

.stats-title {
  margin-top: -20px;
}

.card-title {
  margin: 10px;
}

.modal-body {
  padding: 0px 0px 16px;
}

.modal-body p {
  margin: 5px;
}

.appliance-icons {
  width: 100px;
  border-radius: 3px;
  background: #999;
  padding: 15px;
  margin-top: -15px;
  margin-right: 15px;
  margin-left: 15px;
}

.appliance-on {
  background: linear-gradient(60deg, #66bb6a, #43a047);
  box-shadow: 0 12px 20px -10px rgba(76, 175, 80, 0.28),
    0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2);
}


.appliance-on-light {
  background: #66bb6a
}

.appliance-on-fan {
  background: #039be5
}

.appliance-on-kettle {
  background: #ff7043
}

.stats-layout {
  justify-content: center;
  text-align: center;
}

.pie-chart {
  height: 632px;
}


.md-card-pricing .title {
  margin-top: 5px;
}

</style>
